/* ==========================================================================
   South Steel · Controlador unico de la web uniproducto
   Creado 2026-09-14 · prefijo ssx
   Un solo archivo maneja carrito, variantes, drawer y barra movil.
   No convive con ssp-cart.js ni con los controladores anteriores: el layout
   south-steel.liquid no los carga.
   ========================================================================== */

(function () {
  'use strict';

  if (window.SSX && window.SSX.booted) return;
  var SSX = (window.SSX = window.SSX || {});
  SSX.booted = true;

  var cfg = SSX.config || {};
  var routes = cfg.routes || {};
  var strings = cfg.strings || {};

  /* --- utilidades ------------------------------------------------------- */

  function $(sel, root) {
    return (root || document).querySelector(sel);
  }
  function $$(sel, root) {
    return Array.prototype.slice.call((root || document).querySelectorAll(sel));
  }
  function on(el, ev, fn, opts) {
    if (el) el.addEventListener(ev, fn, opts);
  }

  var nf = null;
  function money(cents) {
    var units = Math.round(Number(cents) || 0) / 100;
    try {
      if (!nf) nf = new Intl.NumberFormat('es-CL', { maximumFractionDigits: 0 });
      return '$' + nf.format(units);
    } catch (e) {
      return '$' + String(Math.round(units)).replace(/\B(?=(\d{3})+(?!\d))/g, '.');
    }
  }
  SSX.money = money;

  function t(key, fallback) {
    return strings[key] || fallback;
  }

  /* Respeta la preferencia del sistema: si el usuario pidio menos
     movimiento, la galeria salta en vez de deslizarse. */
  function reduceMotion() {
    try {
      return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    } catch (e) {
      return false;
    }
  }

  /* --- bloqueo de scroll sin romper iOS --------------------------------- */

  var lockCount = 0;
  var lockY = 0;
  var afterUnlock = [];

  /* Ejecuta fn cuando el body vuelve a desplazarse. Mientras un panel
     cierra, el body sigue fijo: un scroll antes de soltarlo se perderia. */
  function whenUnlocked(fn) {
    if (lockCount === 0) fn();
    else afterUnlock.push(fn);
  }

  function lockScroll() {
    if (lockCount++ > 0) return;
    lockY = window.scrollY || window.pageYOffset || 0;
    var b = document.body;
    b.style.position = 'fixed';
    b.style.top = -lockY + 'px';
    b.style.insetInline = '0';
    b.style.width = '100%';
  }

  function unlockScroll() {
    if (--lockCount > 0) return;
    lockCount = 0;
    var b = document.body;
    b.style.position = '';
    b.style.top = '';
    b.style.insetInline = '';
    b.style.width = '';
    /* 'instant': las paginas de Savor declaran html { scroll-behavior: smooth }
       y la restauracion se animaria desde arriba en vez de quedarse en su sitio. */
    window.scrollTo({ top: lockY, left: 0, behavior: 'instant' });
    var queued = afterUnlock;
    afterUnlock = [];
    queued.forEach(function (fn) {
      fn();
    });
  }

  /* --- foco atrapado ---------------------------------------------------- */

  var FOCUSABLE =
    'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), summary, [tabindex]:not([tabindex="-1"])';

  function focusables(root) {
    return $$(FOCUSABLE, root).filter(function (el) {
      return el.offsetParent !== null || el === document.activeElement;
    });
  }

  /* Estados del panel: closed -> opening -> open -> closing -> closed.
     `state` es la unica fuente de verdad; se refleja en data-state para el CSS.
     El atributo `open` existe mientras el panel es visible (tambien al cerrar),
     asi `[open]` sigue mostrando la capa y ocultando la capsula de Forms (SS-004).
     El fin de cada fase lo marca el transitionend del panel, no un timer. */
  function makeOverlay(el, opts) {
    opts = opts || {};
    var lastFocus = null;
    var panel = (opts.panel && $(opts.panel, el)) || el;
    var state = 'closed';
    var guard = 0;

    function setState(next) {
      state = next;
      el.setAttribute('data-state', next);
    }

    /* Red de seguridad por si transitionend no llega (pestana en segundo
       plano, transicion interrumpida). Se deriva de la duracion del CSS. */
    function cssDuration() {
      var cs = window.getComputedStyle(panel);
      var toMs = function (v) {
        return v.indexOf('ms') > -1 ? parseFloat(v) : parseFloat(v) * 1000;
      };
      var d = cs.transitionDuration.split(',').map(toMs);
      var l = cs.transitionDelay.split(',').map(toMs);
      return Math.max.apply(null, d.map(function (v, i) { return (v || 0) + (l[i] || 0); }));
    }

    function afterTransition(phase, done) {
      var id = ++guard;
      var ms = reduceMotion() ? 0 : cssDuration();
      function finish() {
        if (id !== guard || state !== phase) return;
        guard++;
        panel.removeEventListener('transitionend', onEnd);
        done();
      }
      function onEnd(e) {
        if (e.target === panel && e.propertyName === 'transform') finish();
      }
      if (!ms) {
        finish();
        return;
      }
      panel.addEventListener('transitionend', onEnd);
      window.setTimeout(finish, ms + 80);
    }

    function keydown(e) {
      if (e.key === 'Escape') {
        e.preventDefault();
        close();
        return;
      }
      if (e.key !== 'Tab') return;
      var items = focusables(el);
      if (!items.length) return;
      var first = items[0];
      var last = items[items.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    }

    function open() {
      if (state === 'open' || state === 'opening') return;
      if (state === 'closed') {
        lastFocus = document.activeElement;
        el.setAttribute('open', '');
        el.removeAttribute('aria-hidden');
        lockScroll();
        /* fija la posicion de salida antes de animar hacia dentro */
        void panel.getBoundingClientRect();
      }
      /* desde 'closing' el panel invierte su recorrido desde donde esta */
      document.addEventListener('keydown', keydown);
      setState('opening');
      window.requestAnimationFrame(function () {
        if (state !== 'opening') return;
        var target = opts.initialFocus && $(opts.initialFocus, el);
        var items = focusables(el);
        (target || items[0] || el).focus({ preventScroll: true });
      });
      afterTransition('opening', function () {
        setState('open');
      });
      if (opts.onOpen) opts.onOpen();
    }

    function close() {
      if (state === 'closed' || state === 'closing') return;
      document.removeEventListener('keydown', keydown);
      setState('closing');
      afterTransition('closing', function () {
        setState('closed');
        el.removeAttribute('open');
        el.setAttribute('aria-hidden', 'true');
        /* el foco vuelve antes de soltar el body: si un enlace del menu
           dejo un desplazamiento en cola, ese destino se queda con el foco */
        if (lastFocus && document.contains(lastFocus)) lastFocus.focus({ preventScroll: true });
        unlockScroll();
        if (opts.onClose) opts.onClose();
      });
    }

    setState('closed');

    $$('[data-ssx-close]', el).forEach(function (btn) {
      on(btn, 'click', close);
    });

    return {
      open: open,
      close: close,
      el: el,
      isOpen: function () {
        return state === 'open' || state === 'opening';
      },
    };
  }

  /* ======================================================================
     Carrito
     ====================================================================== */

  var cart = (SSX.cart = {
    busy: false,
    drawer: null,
    listeners: [],
  });

  function cartUrl(path) {
    return routes[path] || path;
  }

  function parseError(data, res) {
    if (data && (data.description || data.message)) {
      return data.description || data.message;
    }
    if (res && res.status === 422) return t('unavailable', 'No pudimos agregar ese producto.');
    return t('generic_error', 'No pudimos completar la accion. Intenta de nuevo.');
  }

  function post(url, body) {
    return fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      body: JSON.stringify(body),
    }).then(function (res) {
      return res
        .json()
        .catch(function () {
          return {};
        })
        .then(function (data) {
          if (!res.ok) {
            var err = new Error(parseError(data, res));
            err.data = data;
            throw err;
          }
          return data;
        });
    });
  }

  function sectionParams() {
    return {
      sections: 'ssx-cart-drawer',
      sections_url: window.location.pathname + window.location.search,
    };
  }

  /* Repinta solo el interior del drawer: la cascara y el foco se conservan. */
  function applySections(payload) {
    if (!payload || !payload.sections) return;
    var html = payload.sections['ssx-cart-drawer'];
    if (!html) return;

    var doc = new DOMParser().parseFromString(html, 'text/html');

    ['#ssx-cart-body', '#ssx-cart-foot'].forEach(function (sel) {
      var next = $(sel, doc);
      var current = $(sel);
      if (next && current) current.innerHTML = next.innerHTML;
    });

    var nextCount = $('[data-ssx-cart-state]', doc);
    if (nextCount) {
      var count = parseInt(nextCount.getAttribute('data-ssx-cart-state'), 10) || 0;
      setCartCount(count);
    }
  }

  function setCartCount(count) {
    $$('[data-ssx-cart-count]').forEach(function (el) {
      el.textContent = String(count);
      el.hidden = count < 1;
    });
    $$('[data-ssx-cart-state]').forEach(function (el) {
      el.setAttribute('data-ssx-cart-state', String(count));
    });
    var label = $('[data-ssx-cart-label]');
    if (label) {
      label.textContent =
        count === 1
          ? t('cart_one', '1 producto')
          : t('cart_many', '%s productos').replace('%s', count);
    }
    document.dispatchEvent(new CustomEvent('ssx:cart:updated', { detail: { count: count } }));
  }

  /* Eventos estandar emitidos por SSX. La sincronizacion externa los ignora
     para no refrescarse a si misma. */
  var ownCartEvents = new WeakSet();

  /* Anuncia un cambio ya confirmado por Shopify con el evento estandar
     shopify:cart:lines-update (mismo contrato que @shopify/events: propiedades
     directas y promesa). La pagina /cart de Savor lo escucha y se refresca. */
  function announceCartChange(action, lines, data) {
    var ev = new Event('shopify:cart:lines-update', { bubbles: true, cancelable: true });
    ev.action = action;
    ev.context = 'cart';
    ev.lines = lines;
    ev.promise = Promise.resolve({
      cart: null,
      detail: { sections: (data && data.sections) || {}, source: 'ssx', didError: false },
    });
    ownCartEvents.add(ev);
    document.dispatchEvent(ev);
  }

  cart.add = function (items, opts) {
    opts = opts || {};
    if (cart.busy) return Promise.reject(new Error('busy'));
    cart.busy = true;
    var body = Object.assign({ items: items }, sectionParams());
    return post(cartUrl('cart_add'), body)
      .then(function (data) {
        applySections(data);
        announceCartChange(
          'add',
          items.map(function (item) {
            return { merchandiseId: item.id, quantity: item.quantity };
          }),
          data
        );
        if (opts.openDrawer !== false) cart.open();
        return data;
      })
      .finally(function () {
        cart.busy = false;
      });
  };

  cart.change = function (line, quantity) {
    if (cart.busy) return Promise.reject(new Error('busy'));
    cart.busy = true;
    var panel = $('.ssx-drawer');
    if (panel) panel.setAttribute('data-busy', 'true');
    var body = Object.assign({ line: line, quantity: quantity }, sectionParams());
    return post(cartUrl('cart_change'), body)
      .then(function (data) {
        applySections(data);
        if (quantity > 0) {
          /* La key solo se incluye si la linea de la respuesta coincide con la
             cantidad pedida; si no, no se inventa. */
          var updated = data.items && data.items[line - 1];
          var confirmed = updated && updated.quantity === quantity;
          announceCartChange('update', [confirmed ? { id: updated.key, quantity: quantity } : { quantity: quantity }], data);
        } else {
          announceCartChange('remove', [{ quantity: 0 }], data);
        }
        return data;
      })
      .finally(function () {
        cart.busy = false;
        if (panel) panel.removeAttribute('data-busy');
      });
  };

  /* Vuelve a pedir a Shopify la seccion del carrito SSX y la repinta.
     La secuencia evita que una respuesta vieja pise a una mas nueva. */
  var refreshSeq = 0;
  cart.refresh = function () {
    var seq = ++refreshSeq;
    /* Sin cabecera Accept: en /cart, 'application/json' devuelve el JSON del
       carrito en vez de las secciones. */
    var url = window.location.pathname + '?sections=ssx-cart-drawer';
    return fetch(url)
      .then(function (res) {
        if (!res.ok) throw new Error('No se pudo actualizar el carrito');
        return res.json();
      })
      .then(function (sections) {
        if (seq !== refreshSeq) return;
        applySections({ sections: sections });
      });
  };

  /* Cambios hechos fuera de SSX (la pagina /cart de Savor) llegan como el
     evento estandar de Shopify. Se emite antes del pedido; su promesa se
     resuelve cuando Shopify confirma, y solo entonces se refresca SSX. */
  function initExternalCartSync() {
    if (cart.externalSyncBound) return;
    cart.externalSyncBound = true;
    document.addEventListener('shopify:cart:lines-update', function (e) {
      if (ownCartEvents.has(e)) return;
      if (!e.promise || typeof e.promise.then !== 'function') return;
      e.promise
        .then(function (result) {
          if (result && result.detail && result.detail.didError) return;
          return cart.refresh();
        })
        .catch(function () {});
    });
  }

  cart.open = function () {
    if (cart.drawer) cart.drawer.open();
  };
  cart.close = function () {
    if (cart.drawer) cart.drawer.close();
  };

  function initDrawer() {
    var el = $('.ssx-drawer');
    if (!el) return;

    cart.drawer = makeOverlay(el, {
      panel: '.ssx-drawer__panel',
      initialFocus: '[data-ssx-close]',
      onOpen: function () {
        document.dispatchEvent(new CustomEvent('ssx:drawer:open'));
      },
      onClose: function () {
        document.dispatchEvent(new CustomEvent('ssx:drawer:close'));
      },
    });

    on($('.ssx-drawer__scrim', el), 'click', cart.drawer.close);

    /* Delegacion: el interior se repinta, por eso no se escuchan los nodos. */
    on(el, 'click', function (e) {
      /* Tarjeta de segunda botella: agrega 1 unidad de esa variante con el
         mismo cart.add de la ficha (repinta el drawer y avisa a /cart). */
      var up = e.target.closest('[data-ssx-upsell-add]');
      if (up) {
        e.preventDefault();
        var vid = parseInt(up.getAttribute('data-ssx-upsell-add'), 10);
        if (!vid || up.disabled || cart.busy) return;
        setLineError('');
        var cards = $$('[data-ssx-upsell-add]', el);
        cards.forEach(function (c) {
          c.disabled = true;
        });
        up.setAttribute('aria-busy', 'true');
        cart
          .add([{ id: vid, quantity: 1 }], { openDrawer: false })
          .then(function () {
            /* la tarjeta ya no existe: el foco se queda dentro del carrito */
            if (!el.contains(document.activeElement) || document.activeElement === document.body) {
              var next = $('#ssx-cart-foot [name="checkout"]', el) || $('[data-ssx-close]', el);
              if (next) next.focus({ preventScroll: true });
            }
          })
          .catch(function (err) {
            setLineError(err.message);
            cards.forEach(function (c) {
              c.disabled = false;
            });
            up.removeAttribute('aria-busy');
          });
        return;
      }

      var btn = e.target.closest('[data-ssx-line-change]');
      if (!btn || btn.disabled) return;
      e.preventDefault();
      var line = btn.getAttribute('data-ssx-line');
      var qty = parseInt(btn.getAttribute('data-ssx-line-change'), 10);
      if (!line) return;
      setLineError('');
      cart.change(line, Math.max(0, qty)).catch(function (err) {
        setLineError(err.message);
      });
    });

    on(el, 'change', function (e) {
      var input = e.target.closest('[data-ssx-line-input]');
      if (!input) return;
      var line = input.getAttribute('data-ssx-line');
      var qty = parseInt(input.value, 10);
      if (isNaN(qty) || qty < 0) qty = 1;
      setLineError('');
      cart.change(line, qty).catch(function (err) {
        setLineError(err.message);
      });
    });

    function setLineError(msg) {
      var box = $('[data-ssx-cart-error]');
      if (!box) return;
      box.textContent = msg || '';
      box.hidden = !msg;
    }

    $$('[data-ssx-cart-open]').forEach(function (btn) {
      if (btn.ssxBound) return;
      btn.ssxBound = true;
      on(btn, 'click', function (e) {
        e.preventDefault();
        cart.open();
      });
    });
  }

  /* ======================================================================
     Encabezado
     ====================================================================== */

  function initHeader() {
    var header = $('.ssx-header');
    if (!header) return;

    function measure() {
      var h = Math.ceil(header.getBoundingClientRect().height || 0);
      document.documentElement.style.setProperty('--ssx-head-h', h + 'px');
    }
    measure();
    on(window, 'resize', measure);
    if ('ResizeObserver' in window) new ResizeObserver(measure).observe(header);

    var menu = $('.ssx-menu');
    if (menu) {
      var overlay = makeOverlay(menu, { panel: '.ssx-menu__panel', initialFocus: '[data-ssx-close]' });
      on($('.ssx-menu__scrim', menu), 'click', overlay.close);
      $$('[data-ssx-menu-open]').forEach(function (btn) {
        on(btn, 'click', function (e) {
          e.preventDefault();
          overlay.open();
        });
      });
      $$('.ssx-menu__link', menu).forEach(function (a) {
        on(a, 'click', function () {
          overlay.close();
        });
      });
    }
  }

  /* ======================================================================
     Componente de compra (galeria + variantes + cantidad + agregar)
     ====================================================================== */

  function initPurchase(root) {
    var data = null;
    try {
      data = JSON.parse($('[data-ssx-variants]', root).textContent);
    } catch (e) {
      return;
    }
    if (!data || !data.variants || !data.variants.length) return;

    var state = {
      variant: data.variants.filter(function (v) { return v.available; })[0] || data.variants[0],
      qty: 1,
    };

    var els = {
      chips: $$('[data-ssx-color]', root),
      name: $('[data-ssx-variant-name]', root),
      price: $('[data-ssx-price]', root),
      compare: $('[data-ssx-compare]', root),
      stock: $('[data-ssx-stock]', root),
      stockText: $('[data-ssx-stock-text]', root),
      qtyInput: $('[data-ssx-qty-input]', root),
      qtyMinus: $('[data-ssx-qty="-1"]', root),
      qtyPlus: $('[data-ssx-qty="1"]', root),
      addBtn: $('[data-ssx-add]', root),
      addLabel: $('[data-ssx-add-label]', root),
      status: $('[data-ssx-status]', root),
      slides: $$('[data-ssx-slide]', root),
      track: $('[data-ssx-track]', root),
    };

    function variantByColor(color) {
      for (var i = 0; i < data.variants.length; i++) {
        if (data.variants[i].option === color) return data.variants[i];
      }
      return null;
    }

    /*
       Posicionamiento, nunca filtrado. La galeria es una sola secuencia y
       ningun slide se oculta, se borra ni se vuelve a construir: elegir un
       color solo desplaza el carrusel hasta la primera foto de ese color.

       El color de cada slide se lee del propio DOM (data-ssx-slide-color),
       asi que la galeria y el selector no pueden desincronizarse. El
       media_index del JSON queda solo como respaldo.
    */
    function firstSlideOfColor(color) {
      for (var i = 0; i < els.slides.length; i++) {
        if (els.slides[i].getAttribute('data-ssx-slide-color') === color) return i;
      }
      return -1;
    }

    function syncGalleryToVariant(v) {
      if (!els.track || !v) return;
      var idx = firstSlideOfColor(v.option);
      if (idx < 0 && v.media_index != null) idx = v.media_index;
      if (idx < 0 || !els.slides[idx]) return;
      /* Cambiar de color salta cinco o diez fotos. Animar ese recorrido
         tarda casi dos segundos y en el telefono se siente lento, asi que
         solo se desliza cuando la foto esta al lado. */
      goToSlide(idx, Math.abs(idx - current) <= 2);
    }

    function render() {
      var v = state.variant;

      els.chips.forEach(function (chip) {
        var isOn = chip.getAttribute('data-ssx-color') === v.option;
        chip.setAttribute('aria-checked', isOn ? 'true' : 'false');
        chip.tabIndex = isOn ? 0 : -1;
      });

      if (els.name) els.name.textContent = v.option;
      if (els.price) els.price.textContent = v.price_formatted;
      if (els.compare) {
        var showCompare = v.compare_formatted && v.compare_cents > v.price_cents;
        els.compare.textContent = showCompare ? v.compare_formatted : '';
        els.compare.hidden = !showCompare;
      }

      if (els.stock) {
        els.stock.setAttribute('data-state', v.available ? 'in' : 'out');
      }
      if (els.stockText) {
        els.stockText.textContent = v.available
          ? t('in_stock', 'Disponible')
          : t('out_stock', 'Agotado por ahora');
      }

      if (els.addBtn) {
        els.addBtn.disabled = !v.available;
        els.addBtn.setAttribute('data-variant-id', v.id);
      }
      if (els.addLabel) {
        els.addLabel.textContent = v.available
          ? t('add', 'Agregar al carrito') + ' · ' + money(v.price_cents * state.qty)
          : t('out_stock', 'Agotado por ahora');
      }

      if (els.qtyInput) els.qtyInput.value = String(state.qty);
      if (els.qtyMinus) els.qtyMinus.disabled = state.qty <= 1;

      document.dispatchEvent(
        new CustomEvent('ssx:variant:change', {
          detail: { variant: v, qty: state.qty, root: root },
        })
      );
    }

    function setColor(color) {
      var v = variantByColor(color);
      if (!v) return;
      state.variant = v;
      syncGalleryToVariant(v);
      render();
    }

    els.chips.forEach(function (chip) {
      on(chip, 'click', function () {
        setColor(chip.getAttribute('data-ssx-color'));
      });
      on(chip, 'keydown', function (e) {
        var keys = ['ArrowRight', 'ArrowDown', 'ArrowLeft', 'ArrowUp'];
        if (keys.indexOf(e.key) === -1) return;
        e.preventDefault();
        var i = els.chips.indexOf(chip);
        var dir = e.key === 'ArrowRight' || e.key === 'ArrowDown' ? 1 : -1;
        var next = els.chips[(i + dir + els.chips.length) % els.chips.length];
        next.focus();
        setColor(next.getAttribute('data-ssx-color'));
      });
    });

    $$('[data-ssx-qty]', root).forEach(function (btn) {
      on(btn, 'click', function () {
        var step = parseInt(btn.getAttribute('data-ssx-qty'), 10);
        state.qty = Math.min(99, Math.max(1, state.qty + step));
        render();
      });
    });

    on(els.qtyInput, 'change', function () {
      var n = parseInt(els.qtyInput.value, 10);
      state.qty = isNaN(n) ? 1 : Math.min(99, Math.max(1, n));
      render();
    });

    function setStatus(msg, kind) {
      if (!els.status) return;
      els.status.textContent = msg || '';
      els.status.setAttribute('data-state', kind || '');
    }

    on(els.addBtn, 'click', function (e) {
      e.preventDefault();
      var v = state.variant;
      if (!v.available || cart.busy) return;

      els.addBtn.disabled = true;
      els.addBtn.setAttribute('data-loading', 'true');
      setStatus(t('adding', 'Agregando…'), 'busy');

      cart
        .add([{ id: v.id, quantity: state.qty }])
        .then(function () {
          setStatus(t('added', 'Agregado al carrito.'), 'ok');
        })
        .catch(function (err) {
          if (err.message !== 'busy') setStatus(err.message, 'error');
        })
        .finally(function () {
          els.addBtn.removeAttribute('data-loading');
          els.addBtn.disabled = !state.variant.available;
        });
    });

    /* Galeria */
    var current = 0;
    var thumbs = $$('[data-ssx-thumb]', root);
    var thumbStrip = $('[data-ssx-thumbs]', root);
    var counter = $('[data-ssx-gal-count]', root);
    var arrows = $$('[data-ssx-arrow]', root);

    function goToSlide(i, smooth) {
      if (!els.track || !els.slides.length) return;
      current = Math.max(0, Math.min(els.slides.length - 1, i));
      var slide = els.slides[current];
      /* Se mide contra el primer slide y no contra offsetParent: asi el
         calculo no depende de que la pista tenga o no position. */
      var base = els.slides[0].offsetLeft;
      els.track.scrollTo({
        left: slide.offsetLeft - base,
        behavior: smooth === false || reduceMotion() ? 'auto' : 'smooth',
      });
      syncGalleryUI();
    }

    function syncGalleryUI() {
      thumbs.forEach(function (th, i) {
        th.setAttribute('aria-current', i === current ? 'true' : 'false');
      });

      /* Con muchas fotos la miniatura activa se sale de pantalla si no se
         la arrastra a la vista. Solo mueve la tira, nunca la pagina. */
      var active = thumbs[current];
      if (active && thumbStrip) {
        var left = active.offsetLeft - thumbStrip.offsetLeft;
        var min = thumbStrip.scrollLeft;
        var max = min + thumbStrip.clientWidth - active.offsetWidth;
        if (left < min || left > max) {
          thumbStrip.scrollTo({
            left: left - thumbStrip.clientWidth / 2 + active.offsetWidth / 2,
            behavior: reduceMotion() ? 'auto' : 'smooth',
          });
        }
      }

      if (counter) {
        counter.textContent = current + 1 + ' / ' + els.slides.length;
      }

      arrows.forEach(function (a) {
        var step = parseInt(a.getAttribute('data-ssx-arrow'), 10);
        a.disabled = step < 0 ? current === 0 : current === els.slides.length - 1;
      });
    }

    thumbs.forEach(function (th, i) {
      on(th, 'click', function () {
        goToSlide(i);
      });
    });

    arrows.forEach(function (a) {
      on(a, 'click', function () {
        goToSlide(current + parseInt(a.getAttribute('data-ssx-arrow'), 10));
      });
    });

    /* Teclado sobre la pista: misma secuencia, sin saltar a otra seccion. */
    on(els.track, 'keydown', function (e) {
      if (e.key !== 'ArrowLeft' && e.key !== 'ArrowRight') return;
      e.preventDefault();
      goToSlide(current + (e.key === 'ArrowRight' ? 1 : -1));
    });

    if (els.track) {
      var raf;
      on(els.track, 'scroll', function () {
        window.cancelAnimationFrame(raf);
        raf = window.requestAnimationFrame(function () {
          var mid = els.track.scrollLeft + els.track.clientWidth / 2;
          for (var i = 0; i < els.slides.length; i++) {
            var s = els.slides[i];
            if (mid >= s.offsetLeft && mid < s.offsetLeft + s.offsetWidth) {
              current = i;
              break;
            }
          }
          syncGalleryUI();
        });
      }, { passive: true });
    }

    render();
    syncGalleryUI();

    root.ssxPurchase = {
      getState: function () {
        return { variant: state.variant, qty: state.qty };
      },
      setColor: setColor,
      focusPicker: function () {
        var on_ = els.chips.filter(function (c) {
          return c.getAttribute('aria-checked') === 'true';
        })[0];
        (on_ || els.chips[0] || root).focus();
      },
    };
  }

  /* ======================================================================
     Barra de compra movil
     ====================================================================== */

  /*
     Los listeners de documento se registran UNA sola vez y vuelven a buscar
     la barra en cada evento. El editor de temas recarga secciones sueltas, y
     si se registraran de nuevo en cada recarga un solo toque en "Agregar"
     terminaria agregando dos veces al carrito.
  */
  var stickyState = { drawerOpen: false, bound: false, raf: 0 };

  /*
     El estado se recalcula del DOM vivo en cada comprobacion, nunca se guarda.
     Antes se deducia del registro de un IntersectionObserver, y ese registro
     lleva la posicion de cuando se encolo: con un gesto rapido que cruza el
     ancla de una vez, el dato llegaba viejo y la barra se quedaba pegada
     (visible sobre el hero al volver arriba, o ausente al bajar de golpe).
  */
  function anchorIsPast() {
    var bar = $('.ssx-bar');
    var id = bar && bar.getAttribute('data-ssx-anchor');
    var anchor = id ? document.getElementById(id) : null;
    if (!anchor) return false;
    return anchor.getBoundingClientRect().bottom <= 0;
  }

  function stickyApply() {
    var bar = $('.ssx-bar');
    if (!bar) return;
    bar.setAttribute(
      'data-show',
      !stickyState.drawerOpen && anchorIsPast() ? 'true' : 'false'
    );
  }

  function stickySchedule() {
    if (stickyState.raf) return;
    stickyState.raf = window.requestAnimationFrame(function () {
      stickyState.raf = 0;
      stickyApply();
    });
  }

  function initStickyBar() {
    var bar = $('.ssx-bar');
    if (!bar) return;

    /* La seccion puede repintarse: se recalcula al vuelo. */
    stickyApply();

    if (stickyState.bound) return;
    stickyState.bound = true;

    /* Un solo listener pasivo, limitado a un fotograma. */
    on(window, 'scroll', stickySchedule, { passive: true });
    on(window, 'resize', stickySchedule);

    document.addEventListener('ssx:drawer:open', function () {
      stickyState.drawerOpen = true;
      stickyApply();
    });
    document.addEventListener('ssx:drawer:close', function () {
      stickyState.drawerOpen = false;
      stickyApply();
    });

    document.addEventListener('ssx:variant:change', function (e) {
      var live = $('.ssx-bar');
      if (!live) return;
      var v = e.detail.variant;
      var color = $('[data-ssx-bar-color]', live);
      var price = $('[data-ssx-bar-price]', live);
      var btn = $('[data-ssx-bar-add]', live);
      if (color) color.textContent = v.option;
      if (price) price.textContent = money(v.price_cents * e.detail.qty);
      if (btn) {
        btn.disabled = !v.available;
        btn.textContent = v.available
          ? t('add_short', 'Agregar')
          : t('out_stock', 'Agotado');
      }
    });

    /* Delegado: la barra puede repintarse sin perder el boton de compra. */
    document.addEventListener('click', function (e) {
      var btn = e.target.closest ? e.target.closest('[data-ssx-bar-add]') : null;
      if (!btn || btn.disabled) return;
      e.preventDefault();
      var main = $('[data-ssx-purchase]');
      if (!main || !main.ssxPurchase) return;
      var st = main.ssxPurchase.getState();
      if (!st.variant || !st.variant.available) {
        /* Si falta una seleccion valida, se lleva al selector. */
        var live = $('.ssx-bar');
        var target = live && document.getElementById(live.getAttribute('data-ssx-anchor'));
        if (target) target.scrollIntoView({ behavior: 'smooth', block: 'start' });
        main.ssxPurchase.focusPicker();
        return;
      }
      btn.disabled = true;
      cart
        .add([{ id: st.variant.id, quantity: st.qty }])
        .catch(function () {})
        .finally(function () {
          btn.disabled = false;
        });
    });
  }

  /* ======================================================================
     Cierre de compra: una o dos botellas
     ====================================================================== */

  function initCloser(root) {
    var data = null;
    try {
      data = JSON.parse($('[data-ssx-variants]', root).textContent);
    } catch (e) {
      return;
    }
    if (!data || !data.variants || !data.variants.length) return;

    var first = data.variants.filter(function (v) { return v.available; })[0] || data.variants[0];
    var state = { mode: '1', a: first, b: first };

    var opts = $$('[data-ssx-opt]', root);
    var status = $('[data-ssx-status]', root);
    var addBtn = $('[data-ssx-close-add]', root);
    var addLabel = $('[data-ssx-close-add-label]', root);

    function byOption(color) {
      for (var i = 0; i < data.variants.length; i++) {
        if (data.variants[i].option === color) return data.variants[i];
      }
      return null;
    }

    function total() {
      if (state.mode === '1') return state.a.price_cents;
      return state.a.price_cents + state.b.price_cents;
    }

    function render() {
      opts.forEach(function (opt) {
        var isOn = opt.getAttribute('data-ssx-opt') === state.mode;
        opt.setAttribute('data-selected', isOn ? 'true' : 'false');
        var radio = $('[role="radio"]', opt) || opt;
        radio.setAttribute('aria-checked', isOn ? 'true' : 'false');
        var body = $('[data-ssx-opt-body]', opt);
        if (body) body.hidden = !isOn;
      });

      $$('[data-ssx-pick]', root).forEach(function (group) {
        var slot = group.getAttribute('data-ssx-pick');
        var selected = slot === 'b' ? state.b : state.a;
        $$('[data-ssx-pick-color]', group).forEach(function (chip) {
          var isOn = chip.getAttribute('data-ssx-pick-color') === selected.option;
          chip.setAttribute('aria-checked', isOn ? 'true' : 'false');
          chip.tabIndex = isOn ? 0 : -1;
        });
      });

      $$('[data-ssx-opt-total]', root).forEach(function (el) {
        var mode = el.getAttribute('data-ssx-opt-total');
        var cents = mode === '1' ? first.price_cents : first.price_cents * 2;
        el.textContent = money(cents);
      });

      var ok = state.mode === '1' ? state.a.available : state.a.available && state.b.available;
      if (addBtn) addBtn.disabled = !ok;
      if (addLabel) {
        addLabel.textContent = ok
          ? t('add', 'Agregar al carrito') + ' · ' + money(total())
          : t('out_stock', 'Agotado por ahora');
      }
    }

    opts.forEach(function (opt) {
      var trigger = $('[data-ssx-opt-trigger]', opt) || opt;
      on(trigger, 'click', function () {
        state.mode = opt.getAttribute('data-ssx-opt');
        render();
      });
      on(trigger, 'keydown', function (e) {
        if (e.key === ' ' || e.key === 'Enter') {
          e.preventDefault();
          state.mode = opt.getAttribute('data-ssx-opt');
          render();
        }
      });
    });

    $$('[data-ssx-pick]', root).forEach(function (group) {
      var slot = group.getAttribute('data-ssx-pick');
      $$('[data-ssx-pick-color]', group).forEach(function (chip) {
        on(chip, 'click', function () {
          var v = byOption(chip.getAttribute('data-ssx-pick-color'));
          if (!v) return;
          if (slot === 'b') {
            /* La segunda botella es una eleccion aparte: no cambia la
               variante activa de la pagina. */
            state.b = v;
            render();
            return;
          }
          state.a = v;
          render();
          /*
             Estado unico de variante: la seccion de compra es la duena.
             Se le pide el cambio a ella y ella emite ssx:variant:change,
             que mueve galeria, barra movil y esta misma seccion. Aqui no
             se crea un segundo controlador ni se reemite el evento.
          */
          var main = $('[data-ssx-purchase]');
          if (main && main.ssxPurchase) main.ssxPurchase.setColor(v.option);
        });
      });
    });

    on(addBtn, 'click', function (e) {
      e.preventDefault();
      if (cart.busy) return;

      /* Dos botellas del mismo color = una linea con cantidad 2. */
      var items;
      if (state.mode === '1') {
        items = [{ id: state.a.id, quantity: 1 }];
      } else if (state.a.id === state.b.id) {
        items = [{ id: state.a.id, quantity: 2 }];
      } else {
        items = [
          { id: state.a.id, quantity: 1 },
          { id: state.b.id, quantity: 1 },
        ];
      }

      addBtn.disabled = true;
      addBtn.setAttribute('data-loading', 'true');
      if (status) {
        status.textContent = t('adding', 'Agregando…');
        status.setAttribute('data-state', 'busy');
      }

      cart
        .add(items)
        .then(function () {
          if (status) {
            status.textContent = t('added', 'Agregado al carrito.');
            status.setAttribute('data-state', 'ok');
          }
        })
        .catch(function (err) {
          if (status && err.message !== 'busy') {
            status.textContent = err.message;
            status.setAttribute('data-state', 'error');
          }
        })
        .finally(function () {
          addBtn.removeAttribute('data-loading');
          render();
        });
    });

    /* Mantiene la seleccion alineada con el componente principal de compra. */
    document.addEventListener('ssx:variant:change', function (e) {
      var v = byOption(e.detail.variant.option);
      if (!v) return;
      state.a = v;
      render();
    });

    render();
  }

  /* ======================================================================
     Acordeones, video y carrusel manual
     ====================================================================== */

  function initAccordions(root) {
    $$('[data-ssx-acc]', root || document).forEach(function (btn) {
      if (btn.ssxBound) return;
      btn.ssxBound = true;
      on(btn, 'click', function () {
        var panel = document.getElementById(btn.getAttribute('aria-controls'));
        var open = btn.getAttribute('aria-expanded') === 'true';
        btn.setAttribute('aria-expanded', open ? 'false' : 'true');
        if (panel) panel.hidden = open;
      });
    });
  }

  function initVideos(root) {
    $$('[data-ssx-video]', root || document).forEach(function (wrap) {
      if (wrap.ssxBound) return;
      wrap.ssxBound = true;
      var video = $('video', wrap);
      var play = $('[data-ssx-play]', wrap);
      if (!video || !play) return;
      on(play, 'click', function () {
        play.hidden = true;
        video.setAttribute('controls', '');
        var p = video.play();
        if (p && p.catch) p.catch(function () {});
      });
      on(video, 'pause', function () {
        if (video.currentTime === 0) play.hidden = false;
      });
    });
  }

  function initCarousels(root) {
    $$('[data-ssx-carousel]', root || document).forEach(function (wrap) {
      if (wrap.ssxBound) return;
      wrap.ssxBound = true;
      var track = $('[data-ssx-carousel-track]', wrap);
      var prev = $('[data-ssx-carousel="prev"]', wrap);
      var next = $('[data-ssx-carousel="next"]', wrap);
      if (!track) return;

      function step() {
        var item = track.firstElementChild;
        return item ? item.offsetWidth + 12 : track.clientWidth * 0.8;
      }
      function sync() {
        var max = track.scrollWidth - track.clientWidth - 2;
        if (prev) prev.disabled = track.scrollLeft <= 2;
        if (next) next.disabled = track.scrollLeft >= max;
      }
      on(prev, 'click', function () {
        track.scrollBy({ left: -step(), behavior: 'smooth' });
      });
      on(next, 'click', function () {
        track.scrollBy({ left: step(), behavior: 'smooth' });
      });
      on(track, 'scroll', sync, { passive: true });
      on(window, 'resize', sync);
      sync();
    });
  }

  /* ======================================================================
     Guarda de modulo vacio

     Si la seccion de opiniones se queda sin contenido real (sin videos y con
     el widget de resenas sin pintar), se oculta en la tienda. Vale mas un
     modulo ausente que un titulo con nada debajo.
     ====================================================================== */

  function initReviewsGuard() {
    var sec = $('[data-ssx-reviews]');
    if (!sec || sec.getAttribute('data-design-mode') === 'true') return;
    if (sec.querySelector('.ssx-vids__item')) return;
    var rev = $('.ssx-reviews', sec);
    if (!rev) return;
    var tries = 0;
    var timer = window.setInterval(function () {
      if (rev.getBoundingClientRect().height > 20) {
        window.clearInterval(timer);
        return;
      }
      if (++tries > 12) {
        window.clearInterval(timer);
        sec.hidden = true;
      }
    }, 300);
  }

  /* ======================================================================
     Shopify Forms

     Shopify Forms es el unico sistema de captura de correo de la web: aqui no
     se construye ningun formulario. Este bloque solo abre el suyo.

     La app vive dentro del shadow root de <shopify-forms-embed>, en estado
     "tease" (la capsula del 10%). No publica una API para abrirla, asi que se
     pulsa su propio disparador. Si la app no esta, los CTA se quedan ocultos:
     vale mas no ofrecer el codigo que ofrecer un boton muerto.
     ====================================================================== */

  function formsHost() {
    return $$('shopify-forms-embed').find(function (host) {
      return host.shadowRoot && !host.closest('[data-ssx-inline-form]');
    }) || null;
  }

  function openForms() {
    var host = formsHost();
    if (!host) return false;
    var root = host.shadowRoot.querySelector('#app-embed');
    if (!root) return false;
    /* Si ya esta desplegado no hay nada que abrir. */
    if (root.getAttribute('data-current-step') !== 'tease') return true;
    var trigger = root.querySelector('button, [role="button"], a');
    if (!trigger) return false;
    trigger.click();
    return true;
  }

  /* Plan B si la app no abre: el formulario de Forms del pie; si no hay,
     la capsula flotante. */
  function formsFallback() {
    var target = $('[data-ssx-inline-form]') || formsHost();
    if (!target) return;
    target.scrollIntoView({
      behavior: reduceMotion() ? 'auto' : 'smooth',
      block: 'center',
    });
  }

  function initFormsLink() {
    var btns = $$('[data-ssx-forms-open]');
    if (!btns.length) return;

    btns.forEach(function (b) {
      if (b.ssxBound) return;
      b.ssxBound = true;
      on(b, 'click', function (e) {
        e.preventDefault();
        if (!openForms()) formsFallback();
      });
    });

    /* Los CTA que nacen ocultos (pie) aparecen solo si el app embed carga. */
    var hidden = btns.filter(function (b) {
      return b.hidden;
    });
    if (!hidden.length) return;
    var tries = 0;
    var timer = window.setInterval(function () {
      if (!formsHost()) {
        /* ~6 s de margen para el app embed. Si no llega, siguen ocultos. */
        if (++tries > 20) window.clearInterval(timer);
        return;
      }
      window.clearInterval(timer);
      hidden.forEach(function (b) {
        b.hidden = false;
      });
    }, 300);
  }

  /* ======================================================================
     Arranque
     ====================================================================== */

  function boot(scope) {
    initAccordions(scope);
    initPolicies(scope);
    initVideos(scope);
    initCarousels(scope);

    $$('[data-ssx-purchase]', scope || document).forEach(function (el) {
      if (!el.ssxBound) {
        el.ssxBound = true;
        initPurchase(el);
      }
    });

    $$('[data-ssx-closer]', scope || document).forEach(function (el) {
      if (!el.ssxBound) {
        el.ssxBound = true;
        initCloser(el);
      }
    });
  }

  /* ======================================================================
     Enlaces a secciones de la misma pagina
     ====================================================================== */

  /* Solo el desplazamiento suave: la posicion final la fija el CSS
     (scroll-margin-top de .ssx-sec bajo el encabezado fijo). No se usa
     scroll-behavior global porque interferiria con unlockScroll(). */
  function initAnchorScroll() {
    if (initAnchorScroll.bound) return;
    initAnchorScroll.bound = true;

    document.addEventListener('click', function (e) {
      if (e.defaultPrevented || e.button !== 0) return;
      if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
      var a = e.target.closest && e.target.closest('.ssx a[href*="#"]');
      if (!a || (a.target && a.target !== '_self') || a.hasAttribute('download')) return;

      var url;
      try {
        url = new URL(a.href, window.location.href);
      } catch (err) {
        return;
      }
      var here = window.location;
      if (url.origin !== here.origin || url.pathname !== here.pathname || url.search !== here.search) return;
      if (url.hash.length < 2) return;

      var target = document.getElementById(decodeURIComponent(url.hash.slice(1)));
      if (!target) return;

      e.preventDefault();
      /* si el enlace esta en el menu movil, se espera a que cierre */
      whenUnlocked(function () {
        target.scrollIntoView({ behavior: reduceMotion() ? 'auto' : 'smooth', block: 'start' });
        if (here.hash !== url.hash) history.pushState(null, '', url.hash);
        /* el lector de pantalla y el teclado continuan desde la seccion */
        if (!target.hasAttribute('tabindex')) target.setAttribute('tabindex', '-1');
        target.focus({ preventScroll: true });
      });
    });
  }

  /* ======================================================================
     Shorts (WEB-004)

     La tarjeta es una miniatura; el iframe de YouTube (dominio sin cookies)
     se crea solo al tocar. Solo un Short suena a la vez: al abrir otro, el
     anterior vuelve a su miniatura.
     ====================================================================== */

  function initShorts() {
    if (initShorts.bound) return;
    initShorts.bound = true;

    document.addEventListener('click', function (e) {
      var btn = e.target.closest && e.target.closest('.ssx-shorts__play');
      if (!btn) return;
      var frame = btn.closest('[data-ssx-short]');
      if (!frame) return;
      var id = frame.getAttribute('data-ssx-short');
      if (!/^[A-Za-z0-9_-]{11}$/.test(id)) return;

      $$('[data-ssx-short] iframe').forEach(function (other) {
        var host = other.parentNode;
        if (host.ssxThumb) host.replaceChild(host.ssxThumb, other);
      });

      var iframe = document.createElement('iframe');
      iframe.src =
        'https://www.youtube-nocookie.com/embed/' + id + '?autoplay=1&playsinline=1&rel=0&modestbranding=1';
      iframe.title = frame.getAttribute('data-ssx-short-title') || 'Video';
      iframe.allow = 'autoplay; encrypted-media; picture-in-picture; fullscreen';
      iframe.allowFullscreen = true;
      frame.ssxThumb = btn;
      frame.replaceChild(iframe, btn);
      iframe.focus();
    });
  }

  /* ======================================================================
     Resumen de politicas (SS · Envio y respaldo)

     Cada dialogo usa makeOverlay(): el mismo sistema del carrito y del menu.
     El boton abre el resumen sin salir de la pagina; el foco vuelve a el.
     ====================================================================== */

  function initPolicies(scope) {
    $$('[data-ssx-policy]', scope || document).forEach(function (el) {
      if (el.ssxOverlay) return;
      el.ssxOverlay = makeOverlay(el, { panel: '.ssx-policy__panel', initialFocus: '[data-ssx-close]' });
      on($('[data-ssx-policy-scrim]', el), 'click', el.ssxOverlay.close);
    });

    if (initPolicies.bound) return;
    initPolicies.bound = true;
    document.addEventListener('click', function (e) {
      var btn = e.target.closest && e.target.closest('[data-ssx-policy-open]');
      if (!btn) return;
      var el = document.getElementById(btn.getAttribute('data-ssx-policy-open'));
      if (!el || !el.ssxOverlay) return;
      e.preventDefault();
      el.ssxOverlay.open();
    });
  }

  function start() {
    initShorts();
    initAnchorScroll();
    initHeader();
    initDrawer();
    boot(document);
    initStickyBar();
    initReviewsGuard();
    initFormsLink();
    initExternalCartSync();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start, { once: true });
  } else {
    start();
  }

  /* El editor de temas recarga secciones sueltas. */
  document.addEventListener('shopify:section:load', function (e) {
    boot(e.target);
    initHeader();
    initDrawer();
    initStickyBar();
    initFormsLink();
  });
})();